<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-modal
            ref="reject-modal"
            :ok-title="$t('Component(Button):reject')"
            :cancel-title="$t('Component(Button):cancel')"
            ok-variant="danger"
            :ok-disabled="isRecipientSelectionRequired && selectedRecipientEmails.length === 0"
            @ok="doReject"
            @hide="rejectCancelled"
            centered
            :title="$t('Component(RejectReasonModal):title')"
            no-close-on-backdrop
        >
            <EmailRecipientSelector
                v-if="isRecipientSelectionRequired"
                v-model="selectedRecipientEmails"
                :emails="recipientEmails"
            />
            <b-form-group
                :label="$t('Component(RejectReasonModal):label')"
                label-for="rejectReason"
            >
                <b-form-textarea
                    id="rejectReason"
                    v-model="rejectReason"
                    :placeholder="$t('Component(RejectReasonModal):placeholder')"
                />
            </b-form-group>
        </b-modal>
        <b-modal
            ref="approveModal"
            :ok-title="$t('Component(Button):approve')"
            :cancel-title="$t('Component(Button):cancel')"
            ok-variant="primary"
            @ok="doApprove"
            :ok-disabled="!isCommentEntered || (isRecipientSelectionRequired && selectedRecipientEmails.length === 0)"
            centered
            :title="$t('Component(Label):comment')"
            no-close-on-backdrop
            @hide="approveCancelled"
        >
            <EmailRecipientSelector
                v-if="isRecipientSelectionRequired"
                v-model="selectedRecipientEmails"
                :emails="recipientEmails"
            />
            <b-form-group
                :label="$t('Component(Label):comment')"
                label-for="approveComment"
            >
                <b-form-textarea
                    id="approveComment"
                    v-model="approveComment"
                    :placeholder="$t('Component(Placeholder):type-here')"
                />
            </b-form-group>
        </b-modal>
        <ExternalResponseModal
            ref="externalResponse"
            @saved="processExternalResponse"
        />
        <RequestLifeCycle
            ref="requestLifeCycle"
            :life-cycle="request.lifeCycle"
        />
        <b-card>
            <b-row class="pb-1">
                <b-col class="text-left">
                    <span class="align-middle">
                        <StatusBadge
                            type="sent"
                            :status="request.status"
                        />
                    </span>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6" lg="6" class="mb-2">
                    <h2 class="text-left pr-1 mb-1">
                        {{ $t(pageLabelTranslation, {name: getProductTitle()}) }}
                    </h2>
                </b-col>
                <b-col md="6" lg="6" class="text-right">
                    <Actions
                        @resendEmail="resendEmail"
                        @close="close"
                        @save="save"
                        @approve="approve"
                        @approvePartially="approvePartially"
                        @reject="reject"
                        @showLifeCycle="showLifeCycle"
                        @externalReply="showExternalReplyModal"
                        :is-external-reply-visible="isExternalReplyButtonVisible"
                        :is-life-cycle-visible="request.lifeCycle.length > 0"
                        :resend-email-visible="isResendButtonVisible"
                        :approve-visible="isApproveButtonVisible"
                        :partial-approve-visible="isPartialApproveButtonVisible"
                        :reject-visible="isRejectButtonVisible"
                        :save-visible="isSaveButtonVisible"
                        :resend-email-title-attribute-text="resendEmailTitleAttributeText"
                        class="float-right"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col md="12" lg="12">
                    <b-tabs content-class="mt-2" justified>
                        <b-tab :title="$t('Component(SentRequestTabs):view-request-label')" @click="hideSaveButton">
                            <ViewRequest
                                :request="request"
                            />
                        </b-tab>
                        <b-tab
                            v-if="isProductCardTabsVisible"
                            :title="$t('Component(ProductCardTabs):general-info-label')"
                            @click="showSaveButton"
                        >
                            <GeneralInfo
                                :product="product"
                                :id="id + ''"
                                :suppliers="suppliers"
                                :has-concern-elements="isProductHasConcernElements"
                                :product-description-document="productDescription"
                                @productDescriptionChange="productDescriptionChange"
                                @suppliersChange="suppliersChange"
                            />
                        </b-tab>
                        <b-tab
                            v-if="isFMDProductTabsVisible"
                            :title="$t('Component(ProductCardTabs):fmd-label')"
                            @click="showSaveButton"
                        >
                            <Overlay :show="loadingFMDDeclarations">
                                <FMDTab
                                    v-if="productId"
                                    :product-id="productId"
                                    :declarations="fmdDeclarations"
                                    :product-weight="characteristicsWeight"
                                    :get-substance-lists="fetchSharedFmdSubstanceLists"
                                    @declarationChange="saveFMDAndMarkIncludedSubstances"
                                    @weightChange="onWeightChange"
                                />
                            </Overlay>
                        </b-tab>
                        <b-tab
                            v-if="isFMDProductTabsVisible"
                            :title="$t('Component(ProductCardTabs):concern-elements-label')"
                            @click="showSaveButton"
                        >
                            <SubstanceListsWithQuestions
                                :product="fmdSubstanceListsProduct"
                                :legislations="request.legislations.map(l => l.id)"
                                :included-substances="fmdIncludedSubstances"
                                :included-list-ids="fmdIncludedListIds"
                                :loading="loadingFMDDeclarations || fmdIncludedListsLoading"
                                :collapse-questionless-referenced-lists="true"
                                radio-name-prefix="sent-request-product-question-for"
                                @listQuestionStateUpdated="listQuestionStateUpdated"
                            />
                        </b-tab>
                        <b-tab
                            v-if="isLegacyProductTabsVisible"
                            :title="$t('Component(ProductCardTabs):concern-elements-label')"
                            @click="showSaveButton"
                            :disabled="!isConcernElementsTabAccessable"
                        >
                            <ConcernElements
                                :product="product"
                                :id="id + ''"
                                :included-substances="includedSubstances"
                                :excluded-substances="excludedSubstances"
                                :legislations="request.legislations.map(l => l.id)"
                                @include="includeConcernSubstance"
                                @exclude="excludeConcernSubstance"
                            />
                        </b-tab>
                        <b-tab
                            v-if="isLegacyProductTabsVisible"
                            :title="$t('Component(ProductCardTabs):composition-label')"
                            @click="showSaveButton"
                        >
                            <Composition
                                :product="product"
                                :id="id + ''"
                                :concern-substances="includedSubstances"
                                :show-components-substances="product.isComplex"
                                :legislations="request.legislations.map(l => l.id)"
                                @hasConcernSubstances="hasConcernSubstances"
                                @substancePropertiesChanged="onSubstancePropertiesChange"
                                @include="includeConcernSubstance"
                                @exclude="excludeConcernSubstance"
                            />
                        </b-tab>
                        <b-tab
                            v-if="isProductCardTabsVisible"
                            :title="$t('Component(ProductCardTabs):verification-label')"
                            @click="showSaveButton"
                        >
                            <Verification
                                :product="product"
                                :id="id + ''"
                                :doc="declarationsOfConformity"
                                :evidence="evidenceReports"
                                :safe-use="safeUseInstructions"
                                :compliant-statement="compliantStatement"
                                :disassembling="disassemblingInstructions"
                                :is-safe-use-applicable="safeUseApplicable"
                                @safeUseApplicableChange="safeUseApplicableChange"
                                @addDoc="addDoC"
                                @removeDoc="removeDoC"
                                @addCompliantStatement="addCompliantStatement"
                                @removeCompliantStatement="removeCompliantStatement"
                                @addReport="addReport"
                                @removeReport="removeReport"
                                @safeUseChange="safeUseChange"
                                @disassemblingChange="disassemblingChange"
                            />
                        </b-tab>
                        <b-tab
                            :disabled="!canRequestReply()"
                            :active="isReplied()"
                            :title="$t('Component(SentRequestTabs):view-approve')"
                            @click="viewApprove"
                        >
                            <div v-if="reply">
                                <Reply
                                    v-if="isLegacyReplyVersionVisible"
                                    :reply="reply"
                                    :request="request"
                                    :substance-lists="substanceLists"
                                />
                                <ReplyV2
                                    v-else
                                    :request="request"
                                    :reply="reply"
                                    :substance-lists="substanceLists"
                                />
                            </div>
                        </b-tab>
                    </b-tabs>
                </b-col>
            </b-row>
        </b-card>
    </b-overlay>
</template>

<script>
import {Conversation} from "@core/components/messenger";
import { BTabs, BModal, BTab, BCard, BRow, BCol, BBadge, BOverlay, BFormTextarea, BFormGroup } from 'bootstrap-vue'
import {ViewRequest, Actions, Reply, ReplyV2, EmailRecipientSelector} from "@/views/request/sent/components";
import {ProductApi, SystemApi} from "@core/api";
import {GeneralInfo, ConcernElements, Composition, Verification, ProductCardMixins} from "@/views/product/components/product-card";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import backendResponse from "@core/mixins/ui/backend-response";
import {RequestStatus} from "@/libs/product";
import StatusBadge from "@/views/request/components/StatusBadge";
import {
    ProductProvider,
    createProductFromCard,
    ProductBreadcrumbs,
    sentRequestProvider
} from "@core/domain/product";
import {getSupplierName} from "@/libs/product";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";
import ExternalResponseModal from "@/views/request/components/ExternalResponseModal.vue";
import {getAllSubstanceLists} from "@core/domain/substance";
import ActiveSubstanceListsMixin from "@/views/product/components/product-card/ActiveSubstanceListsMixin";
import Overlay from "@core/components/common/Overlay.vue";
import dateRenderer from "@core/mixins/date-renderer";
import FMDTab from "@/views/product/components/product-card/fmd/FMDTab.vue";
import {
    FmdDerivedSubstanceListsMixin,
    SubstanceListsWithQuestions,
} from "@/views/product/components/product-card/fmd";

export default {
    mixins: [backendResponse, ActiveSubstanceListsMixin, dateRenderer, FmdDerivedSubstanceListsMixin],
    components: {
        FMDTab, Overlay, ExternalResponseModal, RequestLifeCycle, SubstanceListsWithQuestions,
        BTabs, BTab, BCard, BRow, BCol, BBadge, BOverlay, BModal, BFormTextarea, BFormGroup,
        ViewRequest, Reply, ReplyV2, EmailRecipientSelector, StatusBadge, Conversation, Actions, GeneralInfo, ConcernElements, Composition,
        Verification,
    },
    props: {
        id: {
            required: true
        }
    },
    data() {
        return {
            isSaveButtonVisible: false,
            request: {
                title: '',
                supplier: {
                    title: '',
                    contact: {
                        name: '',
                        communicationChannels: [{details: ''}]
                    }
                },
                product: {
                    identifiers: [{value: ''}],
                    names: [{value: ''}]
                },
                status: null,
                verificationDocuments: [],
                evidenceReportTypes: [],
                lifeCycle: [],
                replyProcessFlow: null,
                supplierNotificationAttempt: {
                    sentAt: null
                }
            },
            rejectReason: '',
            isVewApproveTabActive: false,
            partialApprove: false,
            approveComment: '',
            selectedRecipientEmails: [],
            reply: null,
            showLoading: true,
            resendEmailInProgress: false,
            statuses: RequestStatus,
            ...ProductCardMixins.data,
        };
    },
    methods: {
        processExternalResponse(response) {
            this.showLoading = true;

            const sentRequest = sentRequestProvider(this.id);
            sentRequest.saveExternalResponse(response)
                .then((response) => {
                    this.initialize();
                })
                .finally(() => this.showLoading = false)

        },
        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },

        showExternalReplyModal() {
            this.$refs.externalResponse.show();
        },

        viewApprove() {
            this.isVewApproveTabActive = true;
            this.hideSaveButton();
        },

        isReplied() {
            return RequestStatus.repliedStatuses().includes(this.request.status);
        },

        canRequestReply() {
            return this.isReplied() || RequestStatus.approvedStatuses().includes(this.request.status);
        },

        showSaveButton() {
            this.isReplyButtonVisible = false;
            this.isSaveButtonVisible = true;
        },

        hideSaveButton() {
            this.isSaveButtonVisible = false;
        },
        getProductTitle() {
            if (this.product.names.length > 0) {
                return getSupplierName(this.product) || this.product.names[0].value;
            }

            return '';
        },
        fetchProductRequest() {
            return ProductApi.getSentRequest(this.id).then(r => {
                this.request = r.data;
                createProductFromCard(r.data.product)
                    .getCard()
                    .then(card => {
                        this.initProduct(card);
                    });
            });
        },
        fetchSubstanceLists() {
            if (this.substanceLists.length > 0) {
                return;
            }

            getAllSubstanceLists().then(lists => {
                this.substanceLists = this.activeSubstanceLists(
                    lists,
                    this.request.legislations.map(l => l.id)
                );
            });
        },
        fetchReply() {
            return new Promise((resolve, reject) => {
                if (this.canRequestReply()) {
                    this.fetchSubstanceLists();
                    return ProductApi.getReply(this.request.id).then((r) => {
                        this.reply = r.data;
                        this.resetRecipientSelection();
                        resolve();
                    }).catch(reject);
                } else {
                    resolve();
                }
            });
        },
        fetch(refreshSubstances = false) {
            return new Promise((resolve, reject) => {
                this.fetchProductRequest().then(() => {

                    if (this.isReplied()) {
                        this.isVewApproveTabActive = true;
                    }

                    this.fetchReply().then(resolve);

                    this.fetchProductTabsData(refreshSubstances);
                });
            });
        },
        fetchProductTabsData(refreshSubstances = false) {
            if (!this.isProductCardTabsVisible) {
                return Promise.resolve();
            }

            const tasks = [this.fetchDocuments()];

            if (this.isFMDRequestFlow) {
                tasks.push(
                    this.fetchFMDDeclarations(true)
                        .then(() => this.syncFmdDerivedSubstanceLists(this.fmdDeclarations))
                );
            } else {
                tasks.push(this.fetchSubstances(refreshSubstances));
            }

            return Promise.all(tasks).then(this.updateProductState);
        },
        saveFMDAndMarkIncludedSubstances(change) {
            this.onFMDChange(change);
            return this.syncFmdDerivedSubstanceLists(this.fmdDeclarations);
        },
        approvePartially(comment = '') {
            if (!this.isReplied()) {
                return;
            }

            this.partialApprove = true;

            if (this.showApproveModal()) {
                return;
            }

            this.showLoading = true;
            ProductApi
                .partiallyApproveRequest(this.id, comment, this.getSelectedRecipients())
                .then(() => {
                    return this.onRequestApproved();
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false)
            ;
        },
        approve(comment = '') {
            if (!this.isReplied()) {
                if (this.request.status !== RequestStatus.PARTIALLY_APPROVED) {
                    return;
                }
            }

            this.partialApprove = false;

            if (this.showApproveModal()) {
                return;
            }

            this.showLoading = true;
            ProductApi
                .approveRequest(this.id, comment, this.getSelectedRecipients())
                .then(() => {
                    return this.onRequestApproved();
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false)
            ;
        },
        showApproveModal() {
            if (this.approveComment.length === 0) {
                this.$refs.approveModal.show();
                return true;
            }
            return false;
        },
        doApprove() {
            const comment = this.approveComment;
            if (this.partialApprove) {
                this.approvePartially(comment);
            } else {
                this.approve(comment);
            }
        },
        approveCancelled() {
            this.approveComment = '';
            this.resetRecipientSelection();
        },
        onRequestApproved() {
            this.showDefaultSuccessMessage('Request has been successfully approved');
            ProductProvider(this.request.product.id).requestApproved();
            return this.fetch(true);
        },
        reject() {
            this.$refs['reject-modal'].show();
        },
        rejectCancelled() {
            this.rejectReason = '';
            this.resetRecipientSelection();
        },
        doReject() {
            this.showLoading = true;

            ProductApi
                .rejectRequest(this.id, this.rejectReason, this.getSelectedRecipients())
                .then(() => {
                    this.request.status = RequestStatus.REJECTED;

                    this.$store.dispatch('requestRejected', {
                        request: this.request,
                        reason: this.rejectReason
                    });

                    this.rejectReason = '';
                    this.showDefaultSuccessMessage('Request has been successfully rejected');
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false)
        },
        close() {
            this.$router.push({name: 'sent-requests'});
        },
        resendEmail() {
            if (this.resendEmailInProgress) {
                return;
            }

            this.resendEmailInProgress = true;
            this.showLoading = true;

            SystemApi.email.resendRequestEmail(parseInt(this.id)).then(r => {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Notification',
                        icon: 'BellIcon',
                        text: 'Email has been sent',
                        variant: 'success'
                    },
                });
            }).finally(() => {
                this.resendEmailInProgress = false;
                this.showLoading = false;
            });
        },

        ...ProductCardMixins.methods,

        getProductId() {
            return this.productId;
        },

        initProductBreadcrumbs() {
            const requestId = this.id;
            ProductBreadcrumbs.push(
                'Sent request',
                this.getProductTitle(),
                () => {
                    this.$router.push({
                        name: 'sent-request',
                        params: {
                            id: requestId
                        }
                    })
                }
            )
        },

        initialize() {
            this.reInitData();
            this.fetch().then(() => {
                this.initProductBreadcrumbs();
                this.isSaveButtonVisible = this.isProductCardTabsVisible;
                if (this.isReplied()) {
                    this.isSaveButtonVisible = false;
                }

                this.showLoading = false;
            });
        },
        getSelectedRecipients() {
            return this.selectedRecipientEmails.map(email => ({email}));
        },
        resetRecipientSelection() {
            this.selectedRecipientEmails = this.defaultRecipientEmails.slice();
        },
    },
    created() {
        this.initialize();
    },
    computed: {
        ...ProductCardMixins.computed,
        isApproveButtonVisible() {
            return this.isReplied() || this.request.status === RequestStatus.PARTIALLY_APPROVED;
        },
        isPartialApproveButtonVisible() {
            return this.isReplied();
        },
        resendEmailTitleAttributeText() {
            if (this.request.supplierNotificationAttempt && this.request.supplierNotificationAttempt.sentAt) {
                return this.renderDateTime(this.request.supplierNotificationAttempt.sentAt);
            }
            return '';
        },
        pageLabelTranslation() {
            if (this.$store.getters.isFreeSubscriber) {
                return 'Page(ForwardedRequestCard):Component(Text):label';
            }
            return 'Page(SentRequests):Component(Text):label';
        },
        isCommentEntered() {
            return this.approveComment.length > 3;
        },
        approvalDetails() {
            return this.reply && this.reply.sections
                ? this.reply.sections.approval_details
                : null;
        },
        approvedByCommunicationChannel() {
            return this.approvalDetails && this.approvalDetails.approvedByCommunicationChannel
                ? this.approvalDetails.approvedByCommunicationChannel.trim()
                : '';
        },
        supplierContactEmail() {
            return this.request.supplier
                && this.request.supplier.contact
                && this.request.supplier.contact.email
                ? this.request.supplier.contact.email.trim()
                : '';
        },
        recipientEmails() {
            const emails = [this.approvedByCommunicationChannel, this.supplierContactEmail]
                .filter(Boolean);
            const seen = new Set();

            return emails.filter(email => {
                const normalizedEmail = email.toLowerCase();
                if (seen.has(normalizedEmail)) {
                    return false;
                }
                seen.add(normalizedEmail);
                return true;
            });
        },
        defaultRecipientEmails() {
            const defaultEmail = this.approvedByCommunicationChannel || this.supplierContactEmail;
            return defaultEmail ? [defaultEmail] : [];
        },
        isRecipientSelectionRequired() {
            return this.recipientEmails.length > 1;
        },
        isRejectButtonVisible() {
            return this.isReplied();
        },
        isExternalReplyButtonVisible() {
            return this.request.status === RequestStatus.NEW;
        },
        isResendButtonVisible() {
            return this.request.status === RequestStatus.NEW;
        },
        isProductCardTabsVisible() {
            return this.$store.getters.subscription.isActive && this.request.status === RequestStatus.APPROVED;
        },
        isFMDProductTabsVisible() {
            return this.isProductCardTabsVisible && this.isFMDRequestFlow && Boolean(this.productId);
        },
        isLegacyProductTabsVisible() {
            return this.isProductCardTabsVisible && !this.isFMDRequestFlow;
        },
        productId() {
            return this.product && this.product.id ? this.product.id : null;
        },
        characteristicsWeight() {
            if (!this.product.characteristics) {
                return null;
            }
            return this.product.characteristics.weight || null;
        },
        fmdSubstanceListQuestionStates() {
            const section = this.reply
                && this.reply.sections
                && this.reply.sections.substance_list_question_states
                ? this.reply.sections.substance_list_question_states
                : null;

            return section && Array.isArray(section.states) ? section.states : [];
        },
        fmdSubstanceListsProduct() {
            return {
                ...this.product,
                substanceListQuestionStates: this.fmdSubstanceListQuestionStates,
            };
        },
        isLegacyReplyVersionVisible() {
            return this.reply.hasOwnProperty('sections') === false;
        },
        isFMDRequestFlow() {
            return this.request.replyProcessFlow === this.$store.getters.replyProcessFlow.FMD;
        },
    }
}
</script>
